import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { QRCodeSVG } from "qrcode.react";
import QRLogo from "@/assets/logo/qr-logo.png";
import { ArrowRight, Maximize } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { formatCurrency } from "@/utils/formatCurrency";
import { CancelBookingDialog } from "./CancelBookingDialog";
import { toast } from "sonner";
import type {
  Booking,
  PassengerSummary,
} from "@/types/booking-history/bookinghistorydetail";
import { safeFormatDateTime } from "@/utils/formatDate";
import { useAuthStore } from "@/stores/auth.store";
import { CancelBooking } from "@/services/bookinghistory.service";
import { useState } from "react";

type TripDetailsCardProps = {
  booking: Booking;
  passengersummary: PassengerSummary[];
  paymentId?: string;
};

export function TripDetailsCard({
  booking,
  passengersummary,
  paymentId,
}: TripDetailsCardProps) {
  const [isBookingCancelled, setIsBookingCancelled] = useState(false);

  const handleCancelBooking = async (reason: string) => {
    if (!paymentId) {
      throw new Error("Payment ID is missing.");
    }

    try {
      await CancelBooking({
        agentCompanySellerId:
          useAuthStore.getState().user?.agentCompanySellerId || "",
        paymentId,
        reason,
      });

      setIsBookingCancelled(true);

      toast.success("Your booking has been cancelled successfully.");
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : "Failed to cancel booking. Please try again later.",
      );

      // Important: tell dialog that API failed
      throw error;
    }
  };

  const isCancellationExpired =
    new Date() >= new Date(booking.cancelEligibleBeforeDateTime);

  const isCancellationDisabled =
    isCancellationExpired ||
    booking.paymentStatusTypeKey?.toLowerCase() === "cancel" ||
    isBookingCancelled;

  return (
    <>
      <h2 className="mt-8 text-lg font-semibold text-secondary-foreground">
        Trip Details
      </h2>
      <section className="rounded-2xl mt-2 p-4 bg-card shadow-xl">
        <div className="grid grid-cols-1 items-center gap-4 md:grid-cols-[3fr_7fr]">
          <Dialog>
            <DialogTrigger className="bg-background px-6 py-4 rounded-lg w-full cursor-pointer">
              <div className="relative mx-auto flex size-32 items-center justify-center rounded-xl border-2 border-[#E4EEFC] bg-card md:size-48">
                <div className="md:hidden">
                  <QRCodeSVG
                    size={96}
                    level="H"
                    value={booking.qrCodeValue}
                    imageSettings={{
                      src: QRLogo,
                      width: 28,
                      height: 28,
                      excavate: true,
                      opacity: 1,
                    }}
                  />
                </div>

                <div className="hidden md:block">
                  <QRCodeSVG
                    size={140}
                    level="H"
                    value={booking.qrCodeValue}
                    imageSettings={{
                      src: QRLogo,
                      width: 40,
                      height: 40,
                      excavate: true,
                      opacity: 1,
                    }}
                  />
                </div>
              </div>
              <div className="mt-2 flex items-center justify-center gap-1.5 text-sm font-semibold text-primary">
                <Maximize size={16} className="shrink-0" aria-hidden="true" />
                <span>Click to enlarge QR</span>
              </div>
            </DialogTrigger>
            <DialogContent
              className="sm:max-w-xs md:max-w-md"
              showCloseButton={false}
            >
              <DialogHeader>
                <DialogTitle className="text-lg font-semibold text-secondary-foreground">
                  View QR
                </DialogTitle>
                <DialogDescription className="text-sm font-medium text-[#666]">
                  Increase your screen brightness for easier QR code scanning.
                </DialogDescription>
              </DialogHeader>

              <div className="mx-auto flex size-72 items-center justify-center rounded-xl border-2 border-[#E4EEFC] bg-card">
                <QRCodeSVG
                  size={236}
                  level="H"
                  value={booking.qrCodeValue}
                  imageSettings={{
                    src: QRLogo,
                    width: 60,
                    height: 60,
                    excavate: true,
                    opacity: 1,
                  }}
                />
              </div>

              <div className="text-sm">
                <p className="font-semibold text-[#2E2F3A]">Booking No.</p>
                <p className="text-secondary-foreground font-medium mt-1">
                  {booking.bookingNo}
                </p>
              </div>

              <DialogClose
                render={
                  <Button variant="default" className="w-full cursor-pointer">
                    Close
                  </Button>
                }
              />
            </DialogContent>
          </Dialog>

          <div>
            <p className="text-sm font-semibold flex items-center gap-1 mt-4 text-muted-foreground md:text-base">
              {booking.origin} <ArrowRight size={14} aria-hidden="true" />
              {booking.destination}
            </p>

            <div className="mt-4 flex items-stretch gap-4">
              <div className="flex-1">
                <p className="text-muted text-xs font-medium leading-tight md:text-sm">
                  Departure
                </p>
                <p className="text-sm text-muted-foreground font-medium mt-1 md:text-base">
                  {safeFormatDateTime(booking.departureDateTime)}
                </p>
              </div>

              {booking.returnDateTime &&
                !booking.returnDateTime.startsWith("0001-01-01") && (
                  <>
                    <Separator
                      orientation="vertical"
                      className="w-px bg-border"
                    />
                    <div className="flex-1">
                      <p className="text-muted text-xs font-medium leading-tight md:text-sm">
                        Return
                      </p>
                      <p className="text-sm text-muted-foreground font-medium mt-1 md:text-base">
                        {safeFormatDateTime(booking.returnDateTime)}
                      </p>
                    </div>
                  </>
                )}
            </div>

            <div className="mt-4 border-t border-border pt-4">
              <h3 className="font-semibold text-sm text-secondary-foreground md:text-base">
                Passenger Summary
              </h3>
              <div className="flex gap-4 mt-2">
                {passengersummary.map((passenger) => (
                  <div key={passenger.ticketTypeName}>
                    <p className="text-muted text-xs font-medium leading-tight md:text-sm">
                      <span>{passenger.ticketTypeName.split(" (")[0]}</span>

                      <span className="md:ml-1">
                        <br className="md:hidden" />
                        {passenger.ticketTypeName.includes(" (")
                          ? ` (${passenger.ticketTypeName.split(" (")[1]}`
                          : ""}
                      </span>
                    </p>

                    <p className="text-2xl font-semibold text-heading mt-1 md:text-[32px]">
                      {passenger.quantity}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="mt-4 border-t border-border pt-4">
          <div className="grid grid-cols-1 items-center md:grid-cols-2">
            <div>
              <h3 className="font-semibold text-sm text-secondary-foreground md:text-base">
                Cancellation Eligibility
              </h3>
              <p className="mt-2 text-muted text-sm font-medium leading-tight">
                You can cancel this booking before{" "}
                {safeFormatDateTime(booking.cancelEligibleBeforeDateTime)}.
                After this date, cancellation is no longer allowed.
              </p>
            </div>
            <div className="md:mt-0 flex items-center justify-end">
              <CancelBookingDialog
                onConfirm={handleCancelBooking}
                disabled={isCancellationDisabled}
              />
            </div>
          </div>
        </div>

        <div className="mt-4 flex items-start justify-between gap-4 md:grid md:grid-cols-2">
          <div>
            <p className="text-muted text-xs font-medium leading-tight md:text-sm">
              Purchase Date/Time
            </p>
            <p className="text-sm text-muted-foreground font-medium mt-1 md:text-base">
              {safeFormatDateTime(booking.purchaseDateTime)}
            </p>
          </div>
          <div>
            <p className="text-muted text-xs font-medium leading-tight md:text-sm">
              Total Amount
            </p>
            <p className="text-sm text-muted-foreground font-medium mt-1 md:text-base">
              {formatCurrency(booking.amount)}
            </p>
          </div>
        </div>
      </section>
    </>
  );
}
